/**
 * /studio's words, shared by StudioPage.tsx and the server (index.ts), the
 * way previewCopy.ts is for /preview: the title, description and FAQ a
 * crawler reads before any JS runs, and the JSON-LD, are the same strings the
 * page renders, so they can't drift apart.
 *
 * The feature claims are the home page's Studio list (HomePage.tsx); keep
 * the two in step when Studio gains or loses something.
 */

export const STUDIO_PAGE_PATH = "/studio";

export const STUDIO_TITLE = "Stitch Studio – Free Online Embroidery Design & Digitizing Editor";

export const STUDIO_DESCRIPTION =
  "Stitch Studio is a free embroidery editor in your browser. Start from free patch templates, digitize your own artwork, and export PES, DST, JEF, EXP or VP3.";

export const STUDIO_HEADING = "A free embroidery design and digitizing editor, in your browser";

export const STUDIO_INTRO =
  "Start from a free patch template or your own artwork, add text, fills and satin outlines, and export a stitch file for your machine. Nothing to install, free for now.";

export const STUDIO_FEATURES: { title: string; body: string }[] = [
  {
    title: "Start from a free template",
    body: "Open a ready-made patch design, change the colours or the words, and make it yours.",
  },
  {
    title: "Digitize your own design",
    body: "Import SVG artwork and turn it into fills and satin outlines, ready to stitch.",
  },
  {
    title: "Draw and add text",
    body: "Pen, arc and polygon tools, plus 100+ embroidery fonts for names and lettering.",
  },
  {
    title: "Fills, outlines and textures",
    body: "Satin borders, fills, textures and skews, with a thread colour library to match your threads.",
  },
  {
    title: "Edit files you already have",
    body: "Open a PES or DST file, edit single stitches, and reorder for fewer colour changes.",
  },
  {
    title: "Export for your machine",
    body: "Download PES, DST, JEF, EXP or VP3, with a stitch worksheet to sew from.",
  },
];

export const STUDIO_FAQ: { question: string; answer: string }[] = [
  {
    question: "Is Stitch Studio free?",
    answer: "Yes. Stitch Studio is free for now: design, digitize and export as many designs as you like.",
  },
  {
    question: "Do I need to install anything?",
    answer: "No. Stitch Studio runs in your web browser on a Mac, Windows or Linux computer. There is nothing to download.",
  },
  {
    question: "Which file formats can I export?",
    answer:
      "PES for Brother, DST for Tajima and commercial machines, JEF for Janome, EXP for Bernina, and VP3 for Husqvarna Viking and Pfaff.",
  },
  {
    question: "Can I digitize my own artwork?",
    answer: "Yes. Import an SVG and Stitch Studio turns its shapes into fills and satin outlines you can adjust before you export.",
  },
  {
    question: "Does it work on a phone?",
    answer:
      "It's built for a big screen and a mouse. On a phone, enter your email on this page and we'll send you a link to open on your computer.",
  },
];

export const STUDIO_STRUCTURED_DATA: object[] = [
  {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "Stitch Studio",
    url: "https://studio.embroiderytext.com/",
    description: STUDIO_DESCRIPTION,
    applicationCategory: "DesignApplication",
    operatingSystem: "Any (runs in a web browser)",
    isAccessibleForFree: true,
    offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: STUDIO_FAQ.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: { "@type": "Answer", text: faq.answer },
    })),
  },
];

export function studioMarkdown(): string {
  return `# Stitch Studio

${STUDIO_INTRO}

[Open Stitch Studio](https://studio.embroiderytext.com/)

## What you can do

${STUDIO_FEATURES.map((f) => `- **${f.title}.** ${f.body}`).join("\n")}

## FAQ

${STUDIO_FAQ.map((f) => `**${f.question}**\n${f.answer}`).join("\n\n")}
`;
}
